import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { papelDoNumeroPorId } from '@/lib/constants';
import { formatarTelefone } from '@/lib/telefone';
import {
  DIAS,
  paraConfig,
  paraFormulario,
  recusarHorario,
  resumir,
} from '@/conversas/horario';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import { atualizarNumero, ouvirNumeros } from '@/services/canaisService';

/**
 * Atendimento automático: horário da clínica e as três mensagens de cada número.
 *
 * As mensagens JÁ SAEM hoje — `receberMensagem` chama `saudacaoAplicavel` e
 * `resolverConversa` usa o encerramento. O que não existia era como editá-las
 * sem mexer no banco. Esta seção alcança o que já funcionava.
 *
 * O horário mora na CLÍNICA (`config.horarioAtendimento`) e as mensagens no
 * NÚMERO: é a mesma clínica em qualquer canal, mas a saudação do número de
 * captação não é a mesma da recepção.
 */

const DICA_VARIAVEIS = '{{primeiro_nome}} · {{nome}} · {{clinica}} · {{atendente}}';

/** Rótulo do número como na tela de Canais: papel primeiro, telefone depois. */
function rotuloDoNumero(numero) {
  const papel = papelDoNumeroPorId(numero.papel);
  const tel = numero.telefone ? formatarTelefone(numero.telefone) : 'sem número';
  return `${papel?.label ?? numero.papel ?? 'Número'} — ${tel}`;
}

function LinhaDoDia({ dia, valor, aoMudar, desabilitado }) {
  return (
    <li className={valor.atende ? 'dia-linha' : 'dia-linha fechado'}>
      <label className="dia-check">
        <input
          type="checkbox"
          checked={valor.atende}
          onChange={(e) => aoMudar({ ...valor, atende: e.target.checked })}
          disabled={desabilitado}
        />
        <span className="dia-nome">{dia.nome}</span>
      </label>

      {valor.atende ? (
        <span className="dia-horas">
          <input
            type="time"
            value={valor.inicio}
            onChange={(e) => aoMudar({ ...valor, inicio: e.target.value })}
            disabled={desabilitado}
            aria-label={`Início — ${dia.nome}`}
          />
          <span aria-hidden="true">até</span>
          <input
            type="time"
            value={valor.fim}
            onChange={(e) => aoMudar({ ...valor, fim: e.target.value })}
            disabled={desabilitado}
            aria-label={`Fim — ${dia.nome}`}
          />
        </span>
      ) : (
        <span className="campo-ajuda">Fechado — sai a mensagem de ausência</span>
      )}
    </li>
  );
}

/** As três mensagens de um número, salvas juntas. */
function MensagensDoNumero({ numero, aoSalvar, salvando }) {
  const [rascunho, setRascunho] = useState({
    saudacao: numero.saudacao ?? '',
    saudacaoForaExpediente: numero.saudacaoForaExpediente ?? '',
    encerramento: numero.encerramento ?? '',
  });
  const [salvo, setSalvo] = useState(false);

  // O documento é assinado em tempo real: se outra aba salvar, o rascunho local
  // ficaria desatualizado sem aviso. Só ressincroniza o que NÃO está em edição.
  useEffect(() => {
    setRascunho({
      saudacao: numero.saudacao ?? '',
      saudacaoForaExpediente: numero.saudacaoForaExpediente ?? '',
      encerramento: numero.encerramento ?? '',
    });
    setSalvo(false);
  }, [numero.id, numero.saudacao, numero.saudacaoForaExpediente, numero.encerramento]);

  const mudar = (campo) => (e) => {
    setRascunho((r) => ({ ...r, [campo]: e.target.value }));
    setSalvo(false);
  };

  const campos = [
    {
      id: 'saudacao',
      rotulo: 'Saudação (dentro do horário)',
      ajuda: 'Sai na PRIMEIRA mensagem do contato, uma vez por conversa.',
    },
    {
      id: 'saudacaoForaExpediente',
      rotulo: 'Mensagem de ausência (fora do horário)',
      ajuda: 'Substitui a saudação nos dias e horas fechados. Vazio = nada é enviado.',
    },
    {
      id: 'encerramento',
      rotulo: 'Mensagem de encerramento',
      ajuda: 'Oferecida ao clicar em “Encerrar”. Quem encerra escolhe se envia.',
    },
  ];

  return (
    <div className="cartao numero-mensagens">
      <h3 className="secao-titulo pequeno">{rotuloDoNumero(numero)}</h3>

      {campos.map((c) => (
        <div className="campo" key={c.id}>
          <label htmlFor={`${numero.id}-${c.id}`}>{c.rotulo}</label>
          <textarea
            id={`${numero.id}-${c.id}`}
            rows={2}
            value={rascunho[c.id]}
            onChange={mudar(c.id)}
            disabled={salvando}
          />
          <p className="campo-ajuda">{c.ajuda}</p>
        </div>
      ))}

      <div className="item-acoes">
        <button
          type="button"
          className="btn-secundario compacto"
          onClick={async () => {
            await aoSalvar(numero.id, rascunho);
            setSalvo(true);
          }}
          disabled={salvando}
        >
          {salvando ? 'Salvando…' : 'Salvar mensagens'}
        </button>
        {salvo && <span className="campo-ajuda">Salvo ✓</span>}
      </div>
    </div>
  );
}

export default function SecaoAtendimentoAuto() {
  const { clinicaId, papel } = useAuth();

  const [formulario, setFormulario] = useState(() => paraFormulario(null));
  const [numeros, setNumeros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [horarioSalvo, setHorarioSalvo] = useState(false);

  const podeEditar = papel === 'gestor' || papel === 'superadmin';

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(
      clinicaId,
      (clinica) => {
        setFormulario(paraFormulario(clinica?.config?.horarioAtendimento));
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ler o horário:', e);
        setErro('Não foi possível carregar o horário de atendimento.');
        setCarregando(false);
      },
    );
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirNumeros(
      clinicaId,
      (lista) => setNumeros([...lista].sort((a, b) => (a.papel ?? '').localeCompare(b.papel ?? ''))),
      (e) => {
        console.error('[OdontoSDR] Falha ao ler os números:', e);
        setErro('Não foi possível carregar os números.');
      },
    );
  }, [clinicaId]);

  if (!podeEditar) return null;

  async function salvarHorario() {
    const problema = recusarHorario(formulario);
    if (problema) {
      setErro(problema);
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      // Campo pontilhado: preserva funil, textos de confirmação e o resto da
      // config, que gravar o objeto inteiro apagaria.
      await atualizarCampoConfig(clinicaId, 'horarioAtendimento', paraConfig(formulario));
      setHorarioSalvo(true);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar o horário:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Só o gestor pode alterar o horário.'
          : 'Não foi possível salvar o horário.',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function salvarMensagens(numeroId, campos) {
    setErro(null);
    setSalvando(true);
    try {
      await atualizarNumero(clinicaId, numeroId, campos);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar as mensagens do número:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Só o gestor pode alterar as mensagens.'
          : 'Não foi possível salvar as mensagens.',
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Atendimento automático</h2>
          <p className="secao-sub">
            Horário da clínica e as mensagens que saem sozinhas em cada número.
          </p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="cartao">
        <h3 className="secao-titulo pequeno">Horário de atendimento</h3>
        <p className="campo-ajuda">
          Fora deste horário, quem escrever recebe a <strong>mensagem de ausência</strong> do
          número, no lugar da saudação. Dia desmarcado fica fechado o dia inteiro.
        </p>

        {carregando ? (
          <p className="campo-ajuda">Carregando…</p>
        ) : (
          <>
            <ul className="dias-lista">
              {DIAS.map((d) => (
                <LinhaDoDia
                  key={d.chave}
                  dia={d}
                  valor={formulario[d.chave]}
                  aoMudar={(v) => {
                    setFormulario((f) => ({ ...f, [d.chave]: v }));
                    setHorarioSalvo(false);
                  }}
                  desabilitado={salvando}
                />
              ))}
            </ul>

            <p className="campo-ajuda">{resumir(formulario)}</p>

            <div className="item-acoes">
              <button
                type="button"
                className="btn-primario compacto"
                onClick={salvarHorario}
                disabled={salvando}
              >
                {salvando ? 'Salvando…' : 'Salvar horário'}
              </button>
              {horarioSalvo && <span className="campo-ajuda">Salvo ✓</span>}
            </div>
          </>
        )}
      </div>

      <p className="campo-ajuda dica-variaveis">
        Nos textos abaixo dá para usar: <code>{DICA_VARIAVEIS}</code>
      </p>

      {numeros.length === 0 && (
        <div className="cartao">
          <p className="campo-ajuda">
            Nenhum número cadastrado ainda. Crie um canal na seção acima.
          </p>
        </div>
      )}

      {numeros.map((n) => (
        <MensagensDoNumero key={n.id} numero={n} aoSalvar={salvarMensagens} salvando={salvando} />
      ))}
    </section>
  );
}
